import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HashRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGE_CODES } from './utils/languages';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { AdminStoreProvider } from './store/adminStore';
import { NotesProvider } from './contexts/NotesContext';
import { CalendarProvider } from './contexts/CalendarContext';
import { BreakReminderProvider } from './contexts/BreakReminderContext';
import { ChatProvider } from './contexts/ChatContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ConnectionMonitorProvider } from './contexts/ConnectionMonitorContext';
import Layout from './components/Layout/Layout';
import AuthPage from './pages/Auth/AuthPage';
import NotesPage from './pages/Notes/NotesPage';
import CalendarPage from './pages/Calendar/CalendarPage';
import BreakReminderPage from './pages/BreakReminder/BreakReminderPage';
import ChatPage from './pages/Chat/ChatPage';
import SettingsPage from './pages/Settings/SettingsPage';
import ProfilePage from './pages/Profile/ProfilePage';
import StatisticsPage from './pages/Statistics/StatisticsPage';
import AdminPage from './pages/Admin/AdminPage';
import LockPage from './pages/Lock/LockPage';
import OnboardingModal from './components/Common/OnboardingModal';
import useBreakReminderAlarms from './hooks/useBreakReminderAlarms';
import useCalendarNotifications from './hooks/useCalendarNotifications';
import ToastNotificationContainer from './components/Common/ToastNotification';
import AdminNotificationModal from './components/Common/AdminNotificationModal';
import { useChat } from './contexts/ChatContext';
import { TimeProvider } from './contexts/TimeContext';
import { AdvertisementProvider } from './contexts/AdvertisementContext';
import { LockProvider } from './contexts/LockContext';
import { createAdminUser } from './utils/createAdminUser';
// API URL is managed by Electron IPC - no direct import needed

// Development için: Konsoldan admin fonksiyonları
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.createAdminUser = createAdminUser;
  
  window.makeUserAdmin = async (email) => {
    if (!email) {
      console.error('❌ Email adresi gerekli!');
      console.log('💡 Kullanım: makeUserAdmin("email@example.com")');
      return;
    }
    
    if (typeof window.api?.makeUserAdminByEmail === 'function') {
      try {
        const result = await window.api.makeUserAdminByEmail(email);
        if (result.success) {
          console.log('✅ Başarılı:', result.message);
          console.log('🔄 Sayfayı yenileyin (F5)');
          return result.user;
        } else {
          console.error('❌ Hata:', result.error, result.message);
          return null;
        }
      } catch (error) {
        console.error('❌ Hata:', error);
        return null;
      }
    } else {
      return await createAdminUser(email);
    }
  };
}

// Alarm sistemi bileşeni
function AlarmSystem() {
  useBreakReminderAlarms();
  useCalendarNotifications();
  return null;
}

// Admin bildirim modal handler — ChatProvider içinde olmalı
function AdminNotificationHandler() {
  const { pendingAdminNotification, clearAdminNotification } = useChat();
  if (!pendingAdminNotification) return null;
  return (
    <AdminNotificationModal
      notification={pendingAdminNotification}
      onClose={clearAdminNotification}
    />
  );
}

// Microsoft Store entitlement reconcile (server-side verify — no +30 stacking)
function StoreSubscriptionChecker() {
  const { authToken, refreshUser } = useAuth();
  const checkedRef = React.useRef(false);

  useEffect(() => {
    if (!window.api?.isWindowsStore || !window.api?.storeGetCustomerCollectionsId || !authToken || checkedRef.current) {
      return;
    }
    checkedRef.current = true;

    const check = async () => {
      try {
        const { syncMicrosoftStoreEntitlement } = await import('./utils/syncMicrosoftStoreEntitlement');
        const result = await syncMicrosoftStoreEntitlement({ token: authToken, force: false });
        if (result.ok) {
          await refreshUser();
        }
      } catch (_) {}
    };

    const timer = setTimeout(check, 3000);
    return () => clearTimeout(timer);
  }, [authToken, refreshUser]);

  return null;
}

// Onboarding modal handler
function OnboardingHandler() {
  const { user, markOnboardingComplete, isLoading } = useAuth();
  const [show, setShow] = useState(false);
  const markedRef = React.useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      setShow(false);
      return;
    }

    // Strict boolean: undefined/null must not open the modal as "not completed".
    if (user.onboardingCompleted === true) {
      setShow(false);
      return;
    }

    if (user.onboardingCompleted === false && !markedRef.current) {
      setShow(true);
    }
  }, [isLoading, user]);

  const handleComplete = useCallback(async () => {
    if (markedRef.current) return;
    markedRef.current = true;
    const result = await markOnboardingComplete();
    if (result?.success) {
      setShow(false);
      return;
    }
    markedRef.current = false;
  }, [markOnboardingComplete]);

  return <OnboardingModal isOpen={show} onComplete={handleComplete} />;
}

// Toast event listener bileşeni - NotificationProvider içinde olmalı
// Toast bildirimleri SADECE toast olarak gösterilir, banner'a EKLENMEZ
function ToastEventListener() {
  const lastNotificationRef = React.useRef(null);
  const notificationTimeoutRef = React.useRef(null);

  useEffect(() => {
    const handleToastEvent = (event) => {
      try {
        const { type, title, message } = event.detail || {};
        if (type && title) {
          // Aynı bildirimin tekrar gönderilmesini önle
          const notificationKey = `${type}-${title}-${message}`;
          
          // Eğer aynı bildirim 2 saniye içinde gönderilmişse, yok say
          if (lastNotificationRef.current === notificationKey) {
            if (notificationTimeoutRef.current) {
              clearTimeout(notificationTimeoutRef.current);
            }
            notificationTimeoutRef.current = setTimeout(() => {
              lastNotificationRef.current = null;
            }, 2000);
            return; // Bildirimi gönderme
          }
          
          // Toast bildirimi sadece toast olarak gösterilir, banner'a EKLENMEZ
          // addNotification çağrısı kaldırıldı - toast bildirimleri banner'da gözükmeyecek
          lastNotificationRef.current = notificationKey;
          
          // 2 saniye sonra flag'i temizle
          if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
          }
          notificationTimeoutRef.current = setTimeout(() => {
            lastNotificationRef.current = null;
          }, 2000);
        }
      } catch (error) {
        console.error('Toast event işlenirken hata:', error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-show-toast', handleToastEvent);
    }

    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-show-toast', handleToastEvent);
      }
    };
  }, []); // addNotification dependency'si kaldırıldı

  return null;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('notes');

  // Uygulama dilini başlat: Electron'da kayıtlı tercih ya da sistem dili (desteklenmiyorsa İngilizce),
  // web'de tarayıcı dili (desteklenmiyorsa İngilizce). i18next'teki fallbackLng zaten bu davranışı sağlıyor.
  useEffect(() => {
    const initLocale = async () => {
      try {
        if (window.api?.getInitialLocale) {
          const locale = await window.api.getInitialLocale();
          await i18n.changeLanguage(
            SUPPORTED_LANGUAGE_CODES.includes(locale) ? locale : 'en'
          );
        } else {
          // Web: localStorage tercihi -> tarayıcı dili -> İngilizce
          const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
          const browserLang = (navigator.language || 'en').split('-')[0];
          const locale = SUPPORTED_LANGUAGE_CODES.includes(saved)
            ? saved
            : (SUPPORTED_LANGUAGE_CODES.includes(browserLang) ? browserLang : 'en');
          await i18n.changeLanguage(locale);
        }
      } catch (error) {
        console.warn('Dil başlatılamadı, İngilizce kullanılıyor:', error);
        await i18n.changeLanguage('en');
      }
    };
    initLocale();
  }, []);

  // Dil değişince <html> dir/lang niteliklerini güncelle (RTL diller için — şu an sadece Arapça)
  useEffect(() => {
    const RTL_LANGUAGES = ['ar'];
    const applyDirection = (lng) => {
      const dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
      document.documentElement.dir = dir;
      document.documentElement.lang = lng;
    };
    applyDirection(i18n.language);
    i18n.on('languageChanged', applyDirection);
    return () => i18n.off('languageChanged', applyDirection);
  }, []);

  // Initialize API base URL from Electron IPC on startup - REQUIRED
  useEffect(() => {
    const initializeApiUrl = async () => {
      if (typeof window !== 'undefined' && window.api?.getApiBaseUrl) {
        try {
          // Get URL from Electron main process
          // Always returns https://notesplus-api.bosph.net/api (both development and production)
          const electronUrl = await window.api.getApiBaseUrl();
          if (electronUrl && typeof electronUrl === 'string' && electronUrl.trim() !== '') {
            // Initialize apiConfig with Electron URL
            const { initializeApiUrl: initApiUrl } = await import('./config/apiConfig');
            await initApiUrl();
            const { primeFileUrlCache } = await import('./utils/resolveFileUrl');
            await primeFileUrlCache();
            
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [App] API URL initialized from Electron:', electronUrl);
            }
            return;
          }
          throw new Error('Empty API URL from Electron');
        } catch (error) {
          console.error('❌ [App] Failed to initialize API base URL from Electron:', error);
          // In production, this is fatal - show error to user
          if (process.env.NODE_ENV === 'production') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: i18n.t('shared:app.apiConfigError'),
                message: i18n.t('shared:app.apiConfigErrorMessage'),
              },
            }));
          }
        }
      } else {
        // React dev server mode: try to use ENV
        try {
          const { initializeApiUrl: initApiUrl } = await import('./config/apiConfig');
          await initApiUrl();
          const { primeFileUrlCache } = await import('./utils/resolveFileUrl');
          await primeFileUrlCache();
        } catch (error) {
          console.warn('⚠️ [App] API URL initialization failed (dev mode):', error);
        }
      }
    };
    
    initializeApiUrl();
  }, []);

  // Logout sonrası yönlendirme ve bildirim
  useEffect(() => {
    const handleLogoutComplete = (event) => {
      const { shouldNavigate, shouldShowNotification } = event.detail || {};
      
      // Yönlendirme - Login sayfasına
      if (shouldNavigate) {
        // Kısa bir gecikme ile navigate yap (state güncellemesi için)
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 50);
      }
      
      // Bildirim göster
      if (shouldShowNotification) {
        setTimeout(() => {
          if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'success',
                title: i18n.t('shared:app.logoutSuccess'),
                message: '',
              },
            }));
          }
        }, 100);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-logout-complete', handleLogoutComplete);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-logout-complete', handleLogoutComplete);
      }
    };
  }, [navigate]);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/notes') {
      setActiveSection('notes');
    } else if (path === '/calendar') {
      setActiveSection('calendar');
    } else if (path === '/break-reminder') {
      setActiveSection('break-reminder');
    } else if (path === '/chat') {
      setActiveSection('chat');
    } else if (path === '/settings') {
      setActiveSection('settings');
    } else if (path === '/profile') {
      setActiveSection('profile');
    } else if (path === '/admin') {
      setActiveSection('admin');
    }
    
    // Update presence state - Tab değişimi
    if (typeof window !== 'undefined' && window.api?.updatePresence) {
      const isChatTabActive = path === '/chat' || path.startsWith('/chat');
      window.api.updatePresence({ isChatTabActive });
    }

    // Global Location Tracking: ChatContext'e location'ı zorla yaz (Custom Event ile)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('notesplus-location-update', {
        detail: { pathname: path }
      }));
    }
  }, [location]);

  // Window focus/blur listeners - Presence state güncellemesi
  useEffect(() => {
    if (typeof window === 'undefined' || !window.api?.updatePresence) {
      return;
    }

    const handleFocus = () => {
      window.api.updatePresence({ isFocused: true });
    };

    const handleBlur = () => {
      window.api.updatePresence({ isFocused: false });
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Initial state
    window.api.updatePresence({ isFocused: !document.hidden });

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    if (!window.api?.onNavigateToBreakReminder) {
      return undefined;
    }

    const unsubscribe = window.api.onNavigateToBreakReminder(() => {
      navigate('/break-reminder');
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (!window.api?.onNavigateToChat) {
      return undefined;
    }

    const unsubscribe = window.api.onNavigateToChat((payload) => {
      navigate('/chat');
      // Friend seçimi için event gönder (ChatContext veya ChatPage'de dinlenecek)
      if (payload?.friendId) {
        setTimeout(() => {
          const event = new CustomEvent('notesplus-select-chat-friend', {
            detail: { friendId: payload.friendId },
          });
          window.dispatchEvent(event);
        }, 100);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [navigate]);

  return (
    <ConnectionMonitorProvider>
      <TimeProvider>
        <ThemeProvider>
          <SettingsProvider>
            <AuthProvider>
              <AppRoutes activeSection={activeSection} setActiveSection={setActiveSection} />
            </AuthProvider>
          </SettingsProvider>
        </ThemeProvider>
      </TimeProvider>
    </ConnectionMonitorProvider>
  );
}

// Private Route Component - Sadece authenticated kullanıcılar erişebilir
function PrivateRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-[var(--color-bg-base)]">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#3d8ef0] border-t-transparent mx-auto"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
}

// Public Route Component - Sadece unauthenticated kullanıcılar erişebilir
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/notes', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-[var(--color-bg-base)]">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#3d8ef0] border-t-transparent mx-auto"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return children;
}

function AppRoutes({ activeSection, setActiveSection }) {
  return (
    <Routes>
      {/* Public Routes - Sadece unauthenticated kullanıcılar */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />

      {/* Private Routes - Sadece authenticated kullanıcılar */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AdminProvider>
              <AdminStoreProvider>
                <AdvertisementProvider>
                  <LockProvider>
                    <NotesProvider>
                      <CalendarProvider>
                        <BreakReminderProvider>
                          <ChatProvider>
                            <NotificationProvider>
                              <AlarmSystem />
                              <AdminNotificationHandler />
                              <OnboardingHandler />
                              <StoreSubscriptionChecker />
                              <ToastNotificationContainer />
                              <ToastEventListener />
                              <Layout activeSection={activeSection} setActiveSection={setActiveSection}>
                                <Routes>
                                  <Route path="/" element={<Navigate to="/notes" replace />} />
                                  <Route path="/notes" element={<NotesPage />} />
                                  <Route path="/calendar" element={<CalendarPage />} />
                                  <Route path="/break-reminder" element={<BreakReminderPage />} />
                                  <Route path="/chat" element={<ChatPage />} />
                                  <Route path="/settings" element={<SettingsPage />} />
                                  <Route path="/profile" element={<ProfilePage />} />
                                  <Route path="/statistics" element={<StatisticsPage />} />
                                  <Route path="/admin" element={<AdminPage />} />
                                  <Route path="/redeem" element={<Navigate to="/notes" replace />} />
                                  <Route path="/lock" element={<LockPage />} />
                                  <Route path="*" element={<Navigate to="/notes" replace />} />
                                </Routes>
                              </Layout>
                            </NotificationProvider>
                          </ChatProvider>
                        </BreakReminderProvider>
                      </CalendarProvider>
                    </NotesProvider>
                  </LockProvider>
                </AdvertisementProvider>
              </AdminStoreProvider>
            </AdminProvider>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <HashRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </HashRouter>
  );
}

export default App;

